import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  getAdminStats,
  getPendingTransactions,
  processTransaction,
  getFraudAlerts,
  resolveFraudAlert,
  getAllAccounts,
  getEarnedPaycoins,
  transferPayToUser,
  getAIHouseEdge,
} from "../lib/api";
import { formatCurrency, formatDate, truncateAddress } from "../lib/utils";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Users,
  ArrowDownLeft,
  ArrowUpRight,
  ShieldAlert,
  TrendingUp,
  Check,
  X,
  Brain,
  RefreshCw,
  DollarSign,
  Dices,
  AlertTriangle,
  Coins,
  UserPlus,
  Gamepad2,
  Send,
  Globe,
  Phone,
  Mail,
  Calendar,
  Wallet,
} from "lucide-react";

const severityColors = {
  low: "bg-yellow-500/20 text-yellow-500 border-yellow-500/30",
  medium: "bg-orange-500/20 text-orange-500 border-orange-500/30",
  high: "bg-red-500/20 text-red-500 border-red-500/30",
  critical: "bg-red-700/20 text-red-400 border-red-700/30",
};

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [fraudAlerts, setFraudAlerts] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [earnedPaycoins, setEarnedPaycoins] = useState([]);
  const [aiHouseEdge, setAiHouseEdge] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTx, setSelectedTx] = useState(null);
  const [actionNote, setActionNote] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  
  // PAY Transfer modal state
  const [showPayModal, setShowPayModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [payAmount, setPayAmount] = useState("");
  const [payNote, setPayNote] = useState("");

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, txRes, alertsRes, accountsRes, paycoinsRes] = await Promise.all([
        getAdminStats(),
        getPendingTransactions(),
        getFraudAlerts(),
        getAllAccounts(),
        getEarnedPaycoins(),
      ]);
      setStats(statsRes.data);
      setTransactions(txRes.data.transactions);
      setFraudAlerts(alertsRes.data.alerts);
      setAccounts(accountsRes.data.accounts);
      setEarnedPaycoins(paycoinsRes.data.users);
    } catch (error) {
      console.error("Failed to load admin data:", error);
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  const loadAIRecommendation = async () => {
    try {
      const response = await getAIHouseEdge();
      setAiHouseEdge(response.data);
      toast.success("AI recommendation loaded");
    } catch (error) {
      toast.error("Failed to load AI recommendation");
    }
  };

  const handleTransaction = async (action) => {
    if (!selectedTx) return;
    
    setActionLoading(true);
    try {
      await processTransaction(selectedTx.id, { action, note: actionNote });
      toast.success(`Transaction ${action}d`);
      setSelectedTx(null);
      setActionNote("");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Action failed");
    } finally {
      setActionLoading(false);
    }
  };

  const handleResolveAlert = async (alertId) => {
    try {
      await resolveFraudAlert(alertId);
      toast.success("Alert resolved");
      loadData();
    } catch (error) {
      toast.error("Failed to resolve alert");
    }
  };

  const handleTransferPay = async () => {
    if (!selectedUser || !payAmount) {
      toast.error("Please enter an amount");
      return;
    }

    setActionLoading(true);
    try {
      await transferPayToUser(selectedUser.user_id, {
        amount: parseFloat(payAmount),
        note: payNote || undefined
      });
      toast.success(`Successfully transferred ${payAmount} PAY to ${selectedUser.username}`);
      setShowPayModal(false);
      setSelectedUser(null);
      setPayAmount("");
      setPayNote("");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Transfer failed");
    } finally {
      setActionLoading(false);
    }
  };

  const openPayModal = (user) => {
    setSelectedUser(user);
    setPayAmount("");
    setShowPayModal(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-3xl font-heading font-bold mb-2">Admin Dashboard</h1>
          <p className="text-muted-foreground">
            Manage accounts, transactions, and PAY coin distributions
          </p>
        </motion.div>

        {/* Stats Grid */}
        {stats && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8"
          >
            <div className="glass-card rounded-xl p-6">
              <div className="flex items-center gap-3 mb-2">
                <Users className="w-5 h-5 text-primary" />
                <span className="text-sm text-muted-foreground">Total Users</span>
              </div>
              <div className="text-3xl font-heading font-bold">{stats.total_users}</div>
            </div>
            <div className="glass-card rounded-xl p-6">
              <div className="flex items-center gap-3 mb-2">
                <Dices className="w-5 h-5 text-secondary" />
                <span className="text-sm text-muted-foreground">Total Games</span>
              </div>
              <div className="text-3xl font-heading font-bold">{stats.total_games}</div>
            </div>
            <div className="glass-card rounded-xl p-6">
              <div className="flex items-center gap-3 mb-2">
                <DollarSign className="w-5 h-5 text-primary" />
                <span className="text-sm text-muted-foreground">Total Wagered</span>
              </div>
              <div className="text-2xl font-heading font-bold">
                {formatCurrency(stats.total_wagered)}
              </div>
            </div>
            <div className="glass-card rounded-xl p-6">
              <div className="flex items-center gap-3 mb-2">
                <TrendingUp className="w-5 h-5 text-green-500" />
                <span className="text-sm text-muted-foreground">Platform Profit</span>
              </div>
              <div className={`text-2xl font-heading font-bold ${stats.platform_profit >= 0 ? "text-green-500" : "text-red-500"}`}>
                {formatCurrency(stats.platform_profit)}
              </div>
            </div>
          </motion.div>
        )}

        {/* Alert Counts */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="grid grid-cols-3 gap-4 mb-8"
        >
          <div className="glass-card rounded-xl p-4 border-l-4 border-yellow-500">
            <div className="text-sm text-muted-foreground">Pending Deposits</div>
            <div className="text-2xl font-bold">{stats?.pending_deposits || 0}</div>
          </div>
          <div className="glass-card rounded-xl p-4 border-l-4 border-secondary">
            <div className="text-sm text-muted-foreground">Pending Withdrawals</div>
            <div className="text-2xl font-bold">{stats?.pending_withdrawals || 0}</div>
          </div>
          <div className="glass-card rounded-xl p-4 border-l-4 border-destructive">
            <div className="text-sm text-muted-foreground">Fraud Alerts</div>
            <div className="text-2xl font-bold">{stats?.unresolved_alerts || 0}</div>
          </div>
        </motion.div>

        {/* Main Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Tabs defaultValue="accounts" className="w-full">
            <TabsList className="grid w-full grid-cols-5 mb-6 bg-card">
              <TabsTrigger value="accounts" className="font-heading">
                <UserPlus className="w-4 h-4 mr-2" /> Accounts
              </TabsTrigger>
              <TabsTrigger value="paycoins" className="font-heading">
                <Coins className="w-4 h-4 mr-2" /> Earned PAY
              </TabsTrigger>
              <TabsTrigger value="transactions" className="font-heading">
                <ArrowDownLeft className="w-4 h-4 mr-2" /> Transactions
              </TabsTrigger>
              <TabsTrigger value="fraud" className="font-heading">
                <ShieldAlert className="w-4 h-4 mr-2" /> Fraud
              </TabsTrigger>
              <TabsTrigger value="ai" className="font-heading">
                <Brain className="w-4 h-4 mr-2" /> AI
              </TabsTrigger>
            </TabsList>

            {/* Accounts Tab */}
            <TabsContent value="accounts">
              <div className="glass-card rounded-xl p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-heading font-bold text-lg">All Accounts ({accounts.length})</h3>
                  <Button variant="ghost" size="sm" onClick={loadData}>
                    <RefreshCw className="w-4 h-4 mr-2" /> Refresh
                  </Button>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left py-3 px-2 text-xs text-muted-foreground">ID</th>
                        <th className="text-left py-3 px-2 text-xs text-muted-foreground">User</th>
                        <th className="text-left py-3 px-2 text-xs text-muted-foreground">Email</th>
                        <th className="text-left py-3 px-2 text-xs text-muted-foreground">Mobile</th>
                        <th className="text-left py-3 px-2 text-xs text-muted-foreground">Referral Used</th>
                        <th className="text-left py-3 px-2 text-xs text-muted-foreground">IP Address</th>
                        <th className="text-left py-3 px-2 text-xs text-muted-foreground">Created</th>
                        <th className="text-right py-3 px-2 text-xs text-muted-foreground">Balances</th>
                      </tr>
                    </thead>
                    <tbody>
                      {accounts.map((account) => (
                        <tr key={account.id} className="border-b border-white/5 hover:bg-white/5">
                          <td className="py-3 px-2">
                            <span className="font-mono text-xs text-muted-foreground">
                              {account.id?.slice(0, 8)}...
                            </span>
                          </td>
                          <td className="py-3 px-2">
                            <div className="flex items-center gap-2">
                              <span className="font-medium">{account.username}</span>
                              {account.is_admin && (
                                <Badge className="bg-secondary/20 text-secondary text-xs">Admin</Badge>
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-2">
                            <div className="flex items-center gap-1 text-muted-foreground">
                              <Mail className="w-3 h-3" />
                              <span className="text-xs">{account.email}</span>
                            </div>
                          </td>
                          <td className="py-3 px-2">
                            {account.mobile ? (
                              <div className="flex items-center gap-1 text-muted-foreground">
                                <Phone className="w-3 h-3" />
                                <span className="text-xs">{account.mobile}</span>
                              </div>
                            ) : (
                              <span className="text-xs text-muted-foreground">-</span>
                            )}
                          </td>
                          <td className="py-3 px-2">
                            {account.referral_code_used ? (
                              <Badge variant="outline" className="text-xs border-primary/30 text-primary">
                                {account.referral_code_used}
                              </Badge>
                            ) : (
                              <span className="text-xs text-muted-foreground">None</span>
                            )}
                          </td>
                          <td className="py-3 px-2">
                            {account.registration_ip ? (
                              <div className="flex items-center gap-1 text-muted-foreground">
                                <Globe className="w-3 h-3" />
                                <span className="font-mono text-xs">{account.registration_ip}</span>
                              </div>
                            ) : (
                              <span className="text-xs text-muted-foreground">-</span>
                            )}
                          </td>
                          <td className="py-3 px-2">
                            <div className="flex items-center gap-1 text-muted-foreground">
                              <Calendar className="w-3 h-3" />
                              <span className="text-xs">{formatDate(account.created_at)}</span>
                            </div>
                          </td>
                          <td className="py-3 px-2 text-right">
                            <div className="text-xs">
                              <span className="text-primary font-mono">{formatCurrency(account.balance)}</span>
                              <span className="text-muted-foreground mx-1">/</span>
                              <span className="text-secondary font-mono">{account.pay_balance} PAY</span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </TabsContent>

            {/* Earned Paycoins Tab */}
            <TabsContent value="paycoins">
              <div className="glass-card rounded-xl p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="font-heading font-bold text-lg">Game Activity & PAY Earnings</h3>
                    <p className="text-sm text-muted-foreground">Track user engagement and PAY coin balances</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={loadData}>
                    <RefreshCw className="w-4 h-4 mr-2" /> Refresh
                  </Button>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left py-3 px-2 text-xs text-muted-foreground">User</th>
                        <th className="text-center py-3 px-2 text-xs text-muted-foreground">Games Played</th>
                        <th className="text-center py-3 px-2 text-xs text-muted-foreground">Wins</th>
                        <th className="text-center py-3 px-2 text-xs text-muted-foreground">Win Rate</th>
                        <th className="text-right py-3 px-2 text-xs text-muted-foreground">PAY From Games</th>
                        <th className="text-right py-3 px-2 text-xs text-muted-foreground">Current Balance</th>
                        <th className="text-left py-3 px-2 text-xs text-muted-foreground">SOL Wallet</th>
                        <th className="text-center py-3 px-2 text-xs text-muted-foreground">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {earnedPaycoins.map((user) => (
                        <tr key={user.user_id} className="border-b border-white/5 hover:bg-white/5">
                          <td className="py-3 px-2">
                            <div>
                              <div className="font-medium">{user.username}</div>
                              <div className="text-xs text-muted-foreground">{user.email}</div>
                            </div>
                          </td>
                          <td className="py-3 px-2 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <Gamepad2 className="w-3 h-3 text-muted-foreground" />
                              <span className="font-bold">{user.total_games}</span>
                            </div>
                          </td>
                          <td className="py-3 px-2 text-center">
                            <span className="text-secondary font-bold">{user.wins}</span>
                          </td>
                          <td className="py-3 px-2 text-center">
                            <span className={user.win_rate > 50 ? "text-secondary" : "text-muted-foreground"}>
                              {user.win_rate.toFixed(0)}%
                            </span>
                          </td>
                          <td className="py-3 px-2 text-right">
                            <span className="text-secondary font-mono font-bold">
                              {user.total_pay_from_games} PAY
                            </span>
                          </td>
                          <td className="py-3 px-2 text-right font-mono text-secondary font-bold">
                            {user.current_pay_balance} PAY
                          </td>
                          <td className="py-3 px-2">
                            {user.sol_wallet_address ? (
                              <span className="font-mono text-xs text-muted-foreground">
                                {user.sol_wallet_address.slice(0, 8)}...
                              </span>
                            ) : (
                              <span className="text-xs text-yellow-500">Not set</span>
                            )}
                          </td>
                          <td className="py-3 px-2 text-center">
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-secondary/50 text-secondary hover:bg-secondary/10"
                              onClick={() => openPayModal(user)}
                            >
                              <Send className="w-3 h-3 mr-1" /> Transfer
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {earnedPaycoins.length === 0 && (
                  <div className="text-center py-12 text-muted-foreground">
                    <Coins className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No game data yet</p>
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Transactions Tab */}
            <TabsContent value="transactions">
              <div className="glass-card rounded-xl p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-heading font-bold text-lg">Pending Transactions</h3>
                  <Button variant="ghost" size="sm" onClick={loadData}>
                    <RefreshCw className="w-4 h-4 mr-2" /> Refresh
                  </Button>
                </div>

                {transactions.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Check className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No pending transactions</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {transactions.map((tx) => (
                      <div
                        key={tx.id}
                        className="flex items-center justify-between p-4 rounded-lg bg-background/50 border border-white/5"
                        data-testid={`admin-tx-${tx.id}`}
                      >
                        <div className="flex items-center gap-4">
                          <div
                            className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                              tx.type === "deposit" ? "bg-primary/20" : tx.type === "pay_withdraw" ? "bg-secondary/20" : "bg-accent/20"
                            }`}
                          >
                            {tx.type === "deposit" ? (
                              <ArrowDownLeft className="w-5 h-5 text-primary" />
                            ) : tx.type === "pay_withdraw" ? (
                              <Coins className="w-5 h-5 text-secondary" />
                            ) : (
                              <ArrowUpRight className="w-5 h-5 text-accent" />
                            )}
                          </div>
                          <div>
                            <div className="font-medium">
                              {tx.username} - <span className="capitalize">{tx.type.replace('_', ' ')}</span>
                            </div>
                            <div className="text-sm text-muted-foreground">
                              {truncateAddress(tx.wallet_address, 8)}
                              {tx.tx_hash && (
                                <span className="ml-2">TX: {truncateAddress(tx.tx_hash, 6)}</span>
                              )}
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-4">
                          <div className="text-right">
                            <div className="font-mono font-bold text-lg">
                              {tx.type === "pay_withdraw" ? `${tx.amount} PAY` : formatCurrency(tx.amount)}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {formatDate(tx.created_at)}
                            </div>
                          </div>
                          <Button
                            size="sm"
                            onClick={() => setSelectedTx(tx)}
                            className="bg-primary hover:bg-primary/90"
                          >
                            Review
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>

            {/* Fraud Alerts Tab */}
            <TabsContent value="fraud">
              <div className="glass-card rounded-xl p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-heading font-bold text-lg">Fraud Alerts</h3>
                  <Badge variant="outline" className="border-destructive/50 text-destructive">
                    {fraudAlerts.length} Active
                  </Badge>
                </div>

                {fraudAlerts.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <ShieldAlert className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>No active fraud alerts</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {fraudAlerts.map((alert) => (
                      <div
                        key={alert.id}
                        className={`p-4 rounded-lg border ${severityColors[alert.severity]}`}
                        data-testid={`fraud-alert-${alert.id}`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex items-start gap-3">
                            <AlertTriangle className="w-5 h-5 mt-1" />
                            <div>
                              <div className="font-medium mb-1">
                                {alert.username} - {alert.alert_type}
                              </div>
                              <p className="text-sm opacity-80 mb-2">{alert.description}</p>
                              <div className="text-sm opacity-60 italic">
                                AI Analysis: {alert.ai_analysis}
                              </div>
                              <div className="text-xs opacity-50 mt-2">
                                {formatDate(alert.created_at)}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className={severityColors[alert.severity]}>
                              {alert.severity}
                            </Badge>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleResolveAlert(alert.id)}
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>

            {/* AI Insights Tab */}
            <TabsContent value="ai">
              <div className="glass-card rounded-xl p-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="font-heading font-bold text-lg flex items-center gap-2">
                    <Brain className="w-5 h-5 text-primary" /> AI House Edge Optimization
                  </h3>
                  <Button onClick={loadAIRecommendation} variant="outline">
                    <RefreshCw className="w-4 h-4 mr-2" /> Get Recommendation
                  </Button>
                </div>

                {aiHouseEdge ? (
                  <div className="grid md:grid-cols-3 gap-6">
                    <div className="p-6 rounded-lg bg-primary/10 border border-primary/30">
                      <div className="text-sm text-muted-foreground mb-2">Under 7 Multiplier</div>
                      <div className="text-4xl font-heading font-bold text-primary">
                        {aiHouseEdge.under}x
                      </div>
                    </div>
                    <div className="p-6 rounded-lg bg-secondary/10 border border-secondary/30">
                      <div className="text-sm text-muted-foreground mb-2">Exactly 7 Multiplier</div>
                      <div className="text-4xl font-heading font-bold text-secondary">
                        {aiHouseEdge.exactly}x
                      </div>
                    </div>
                    <div className="p-6 rounded-lg bg-accent/10 border border-accent/30">
                      <div className="text-sm text-muted-foreground mb-2">Over 7 Multiplier</div>
                      <div className="text-4xl font-heading font-bold text-accent">
                        {aiHouseEdge.over}x
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <Brain className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Click "Get Recommendation" to analyze platform data</p>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </motion.div>

        {/* Transaction Review Modal */}
        <Dialog open={!!selectedTx} onOpenChange={() => setSelectedTx(null)}>
          <DialogContent className="bg-card border-white/10">
            <DialogHeader>
              <DialogTitle className="font-heading">
                Review {selectedTx?.type === "deposit" ? "Deposit" : selectedTx?.type === "pay_withdraw" ? "PAY Withdrawal" : "Withdrawal"}
              </DialogTitle>
            </DialogHeader>

            {selectedTx && (
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-muted-foreground">User</div>
                    <div className="font-medium">{selectedTx.username}</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Amount</div>
                    <div className="font-mono font-bold text-lg">
                      {selectedTx.type === "pay_withdraw" ? `${selectedTx.amount} PAY` : formatCurrency(selectedTx.amount)}
                    </div>
                  </div>
                </div>

                <div>
                  <div className="text-sm text-muted-foreground">Wallet Address</div>
                  <div className="font-mono text-sm break-all bg-background/50 p-2 rounded">
                    {selectedTx.wallet_address}
                  </div>
                </div>

                {selectedTx.tx_hash && (
                  <div>
                    <div className="text-sm text-muted-foreground">Transaction Hash</div>
                    <div className="font-mono text-sm break-all bg-background/50 p-2 rounded">
                      {selectedTx.tx_hash}
                    </div>
                  </div>
                )}

                <div>
                  <div className="text-sm text-muted-foreground mb-2">Admin Note (optional)</div>
                  <Textarea
                    value={actionNote}
                    onChange={(e) => setActionNote(e.target.value)}
                    placeholder="Add a note..."
                    className="bg-background/50 border-white/10"
                  />
                </div>

                <div className="flex gap-3">
                  <Button
                    onClick={() => handleTransaction("approve")}
                    disabled={actionLoading}
                    className="flex-1 bg-primary hover:bg-primary/90"
                  >
                    <Check className="w-4 h-4 mr-2" /> Approve
                  </Button>
                  <Button
                    onClick={() => handleTransaction("reject")}
                    disabled={actionLoading}
                    variant="destructive"
                    className="flex-1"
                  >
                    <X className="w-4 h-4 mr-2" /> Reject
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* PAY Transfer Modal */}
        <Dialog open={showPayModal} onOpenChange={setShowPayModal}>
          <DialogContent className="bg-card border-white/10">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <Coins className="w-5 h-5 text-secondary" />
                Transfer PAY Coins
              </DialogTitle>
              <DialogDescription>
                Manually transfer PAY coins to {selectedUser?.username}'s account
              </DialogDescription>
            </DialogHeader>

            {selectedUser && (
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4 p-4 bg-background/50 rounded-lg">
                  <div>
                    <div className="text-xs text-muted-foreground">User</div>
                    <div className="font-medium">{selectedUser.username}</div>
                    <div className="text-xs text-muted-foreground">{selectedUser.email}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-muted-foreground">Current PAY Balance</div>
                    <div className="font-mono font-bold text-secondary">
                      {selectedUser.current_pay_balance} PAY
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="p-3 rounded-lg bg-muted/50">
                    <div className="text-xs text-muted-foreground">Games Played</div>
                    <div className="font-mono font-bold">{selectedUser.total_games}</div>
                  </div>
                  <div className="p-3 rounded-lg bg-secondary/10 border border-secondary/20">
                    <div className="text-xs text-muted-foreground">PAY From Games</div>
                    <div className="font-mono font-bold text-secondary">{selectedUser.total_pay_from_games} PAY</div>
                  </div>
                </div>

                {selectedUser.sol_wallet_address ? (
                  <div className="p-3 rounded-lg bg-muted/50">
                    <div className="text-xs text-muted-foreground mb-1">SOL Wallet</div>
                    <div className="font-mono text-xs break-all">{selectedUser.sol_wallet_address}</div>
                  </div>
                ) : (
                  <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-yellow-500" />
                    <span className="text-sm text-yellow-500">User has not provided a SOL wallet address</span>
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Amount (PAY)</Label>
                  <Input
                    type="number"
                    value={payAmount}
                    onChange={(e) => setPayAmount(e.target.value)}
                    placeholder="Enter PAY amount"
                    className="bg-background/50 border-white/10 h-12 font-mono"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Note (optional)</Label>
                  <Textarea
                    value={payNote}
                    onChange={(e) => setPayNote(e.target.value)}
                    placeholder="Add a note for this transfer..."
                    className="bg-background/50 border-white/10"
                  />
                </div>

                <Button
                  onClick={handleTransferPay}
                  disabled={actionLoading || !payAmount}
                  className="w-full bg-secondary hover:bg-secondary/90 font-heading font-bold"
                >
                  {actionLoading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" /> Transfer {payAmount || 0} PAY
                    </>
                  )}
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
